import { useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, ChevronsUpDown } from "lucide-react";
import {
  SidebarProvider,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarInset,
  SidebarTrigger,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { NAV_CONFIG } from "@/design-system/navigation/nav-config/navConfig";
import { supabase } from "@/supabaseClient.jsx";

/**
 * AppShell — layout base (shadcn Sidebar) para os 3 dashboards autenticados.
 *
 * Props:
 *   role        "baba" | "familia" | "admin"
 *   perfil      objeto do usuário (nome, sobrenome, foto_url_display)
 *   activeTab   string — aba ativa atual
 *   onTabChange (key: string) => void
 *   children    conteúdo da aba atual
 */
export default function AppShell({ role, perfil, activeTab, onTabChange, children }) {
  const navigate = useNavigate();
  const navItems = NAV_CONFIG[role] || [];

  const displayName = perfil?.nome
    ? role === "familia"
      ? `Família ${perfil.sobrenome || ""}`
      : perfil.nome
    : role === "admin"
      ? "Admin"
      : "Usuário";

  const initials = displayName.trim().slice(0, 2).toUpperCase();

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut({ scope: "local" });
      await supabase.auth.signOut({ scope: "global" });
    } catch (error) {
      console.error("Falha no logout:", error);
      localStorage.removeItem("user_type");
    } finally {
      navigate("/", { replace: true });
    }
  };

  return (
    <TooltipProvider>
    <SidebarProvider>
      <Sidebar collapsible="icon">
        <SidebarHeader>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 px-1.5 py-1"
          >
            <img
              src="/assets/logo-tesourinhos-trans.png"
              alt="Tesourinhos"
              className="h-7 w-auto"
            />
          </button>
        </SidebarHeader>

        <SidebarSeparator />

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item) => (
                  <SidebarMenuItem key={item.key}>
                    <SidebarMenuButton
                      isActive={activeTab === item.key}
                      onClick={() => onTabChange(item.key)}
                      tooltip={item.label}
                    >
                      <item.icon />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton size="lg">
                <Avatar size="sm">
                  <AvatarImage src={perfil?.foto_url_display} alt={displayName} />
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
                <span className="truncate">{displayName}</span>
                <ChevronsUpDown className="ml-auto" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-56">
              <DropdownMenuItem
                onClick={() =>
                  navigate(`/dashboard-${role === "familia" ? "familia" : role}`)
                }
              >
                <LayoutDashboard /> Painel
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onClick={handleLogout}>
                <LogOut /> Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset>
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
          <SidebarTrigger />
          <span className="font-heading text-sm font-semibold text-foreground">
            {navItems.find((item) => item.key === activeTab)?.label || ""}
          </span>
        </header>
        <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6">{children}</div>
      </SidebarInset>
    </SidebarProvider>
    </TooltipProvider>
  );
}
